import fluent, { FluentIterable } from "fluent-iterable";
import { Change, ChangeSet } from "../DataStore/ChangeSet";
import { DataProvider, DataProviderArgs } from "../DataStore/DataProvider";
import { GEOMETRY_MEDIA_TYPES, MediaType, getMediaTypeFromMimeType } from "../Media";
import { IMedia, IPlace } from "../models/Models";
import { Kernel } from "../Kernel";
import { PlaceTable } from "../backend/cloud/PlaceTable";

export type Geometry = {
    id: string;
    owner: string;
    type: MediaType;
};

export type MediaGeometry = Geometry & {
    media: IMedia;
};

type OwnerItems = {
    items: Geometry[];
};

// Place array -> Filter media -> Filter

export class GeoProvider extends DataProvider {
    private _items = new Map<string, Geometry>();
    private _owners = new Map<string, OwnerItems>();

    constructor(args: DataProviderArgs) {
        super(args, [
            {
                source: "places",
                paths: ["data"],
            },
        ]);
    }

    override async onChangeSet(source: DataProvider, changeSet: ChangeSet<IPlace>): Promise<void> {
        if (source.name === "places") {
            const thisChangeSet = new ChangeSet<Geometry>();
            await this._placeCreate(thisChangeSet, changeSet.created);
            this._placeDelete(thisChangeSet, changeSet.deleted);
            this._placeUpdate(thisChangeSet, changeSet.updated);
            this._notifyListeners(thisChangeSet);
        }
    }

    public override async all(): Promise<FluentIterable<Geometry>> {
        return fluent(this._items.values());
    }

    private _addItem(ownerId: string, item: Geometry) {
        let owner = this._owners.get(ownerId);
        if (!owner) {
            owner = { items: [] };
            this._owners.set(ownerId, owner);
        }
        owner.items.push(item);
        this._items.set(item.id, item);
    }

    private _removeItem(item: Geometry) {
        this._items.delete(item.id);
        const owner = this._owners.get(item.owner);
        if (owner) {
            owner.items = owner.items.filter((x) => x.id !== item.id);
            if (owner.items.length === 0) {
                this._owners.delete(item.owner);
            }
        }
    }

    private async _placeCreate(changeSet: ChangeSet<Geometry>, created: IPlace[]) {
        // create all survey
        const table = Kernel.instance().dataStore.getSource<PlaceTable>("places");
        for (const p of created) {
            const placeMediaItems = await table.getMediaItems(p.id);
            if (placeMediaItems) {
                for (const placeMedia of placeMediaItems) {
                    const media = placeMedia.media;
                    const type = getMediaTypeFromMimeType(media.mimeType);
                    const id = media.id;
                    if (GEOMETRY_MEDIA_TYPES.includes(type)) {
                        const newItem = {
                            id,
                            owner: p.id,
                            type: type,
                            media,
                        } as MediaGeometry;

                        this._addItem(p.id, newItem);
                        changeSet.created.push(newItem);
                    }
                }
            }
        }
    }

    private _placeDelete(changeSet: ChangeSet<Geometry>, deleted: IPlace[]) {
        // remove all elements associated with this place
        for (const place of deleted) {
            const owner = this._owners.get(place.id);
            if (owner) {
                for (const item of owner.items) {
                    changeSet.deleted.push(item);
                    this._removeItem(item);
                }
            }
        }
    }

    private _placeUpdate(changeSet: ChangeSet<Geometry>, updated: Change<IPlace>[]) {
        // We just remove and read anything. updates are so rare that we
        // don't need to do much clever.
        for (const change of updated) {
            this._placeDelete(changeSet, [change.oldValue]);
            this._placeCreate(changeSet, [change.newValue]);
        }
    }
}
